import React, { createContext, useContext, useReducer, ReactNode } from 'react'
import type { PHIPrediction, SelectedSeries, EditingBox, InstanceMetadata, SeriesCache } from '../../../types/phi'
import { StudyModel } from '@common/config/api/client'

// State interface
export interface PHIRedactionState {
  // Series and image state
  selectedSeries: SelectedSeries
  currentImageId: string | null
  dataToRender: Record<string, StudyModel>
  urlToMetadataMap: Record<string, InstanceMetadata>
  seriesCache: Record<string, SeriesCache>

  // PHI predictions and redactions
  phiPredictions: Record<string, PHIPrediction[]>
  redactedSeries: Set<string>
  redactedSOPInstances: Record<string, Set<string>>

  // UI state
  isDrawingMode: boolean
  isEditMode: boolean
  currentEditingBox: EditingBox | null
  showRedactions: boolean
  boxesDrawn: boolean

  // Loading state
  isLoading: boolean
  imagesLoadedCount: number
  totalImagesToLoad: number
  allImagesLoaded: boolean
  totalImagesInSeries: number
  loadedImageCount: number

  // Workflow state
  phiWorkflowInProgress: boolean
  conversionInProgress: boolean
  phiWorkflowStatus: string
}

// Action types
export type PHIRedactionAction =
  | { type: 'SET_SELECTED_SERIES'; payload: SelectedSeries }
  | { type: 'SET_CURRENT_IMAGE_ID'; payload: string | null }
  | { type: 'SET_DATA_TO_RENDER'; payload: Record<string, StudyModel> }
  | { type: 'SET_URL_TO_METADATA_MAP'; payload: Record<string, InstanceMetadata> }
  | { type: 'UPDATE_SERIES_CACHE'; payload: { seriesId: string; cache: SeriesCache } }
  | { type: 'SET_PHI_PREDICTIONS'; payload: Record<string, PHIPrediction[]> }
  | {
      type: 'UPDATE_PHI_PREDICTION'
      payload: { sopInstanceUid: string; index: number; updates: Partial<PHIPrediction> }
    }
  | { type: 'ADD_PHI_PREDICTION'; payload: { sopInstanceUid: string; prediction: PHIPrediction } }
  | { type: 'REMOVE_PHI_PREDICTION'; payload: { sopInstanceUid: string; predictionId: string } }
  | { type: 'TOGGLE_DRAWING_MODE' }
  | { type: 'TOGGLE_EDIT_MODE' }
  | { type: 'SET_EDIT_MODE'; payload: boolean }
  | { type: 'SET_CURRENT_EDITING_BOX'; payload: EditingBox | null }
  | { type: 'SET_SHOW_REDACTIONS'; payload: boolean }
  | { type: 'SET_BOXES_DRAWN'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_IMAGES_LOADED_COUNT'; payload: number }
  | { type: 'SET_TOTAL_IMAGES_TO_LOAD'; payload: number }
  | { type: 'SET_ALL_IMAGES_LOADED'; payload: boolean }
  | { type: 'SET_TOTAL_IMAGES_IN_SERIES'; payload: number }
  | { type: 'SET_LOADED_IMAGE_COUNT'; payload: number }
  | { type: 'SET_PHI_WORKFLOW_IN_PROGRESS'; payload: boolean }
  | { type: 'SET_CONVERSION_IN_PROGRESS'; payload: boolean }
  | { type: 'SET_PHI_WORKFLOW_STATUS'; payload: string }
  | { type: 'RESET_LOADING_STATE' }

// Initial state
const initialState: PHIRedactionState = {
  selectedSeries: {
    series: '',
    study: '',
    exam_metadata_id: '',
    series_metadata_id: '',
  },
  currentImageId: null,
  dataToRender: {},
  urlToMetadataMap: {},
  seriesCache: {},
  phiPredictions: {},
  redactedSeries: new Set(),
  redactedSOPInstances: {},
  isDrawingMode: false,
  isEditMode: false,
  currentEditingBox: null,
  showRedactions: true,
  boxesDrawn: false,
  isLoading: false,
  imagesLoadedCount: 0,
  totalImagesToLoad: 0,
  allImagesLoaded: false,
  totalImagesInSeries: 0,
  loadedImageCount: 0,
  phiWorkflowInProgress: false,
  conversionInProgress: false,
  phiWorkflowStatus: 'idle',
}

// Reducer function
function phiRedactionReducer(state: PHIRedactionState, action: PHIRedactionAction): PHIRedactionState {
  switch (action.type) {
    case 'SET_SELECTED_SERIES':
      return { ...state, selectedSeries: action.payload }

    case 'SET_CURRENT_IMAGE_ID':
      return { ...state, currentImageId: action.payload }

    case 'SET_DATA_TO_RENDER':
      return { ...state, dataToRender: action.payload }

    case 'SET_URL_TO_METADATA_MAP':
      return { ...state, urlToMetadataMap: action.payload }

    case 'UPDATE_SERIES_CACHE':
      return {
        ...state,
        seriesCache: {
          ...state.seriesCache,
          [action.payload.seriesId]: action.payload.cache,
        },
      }

    case 'SET_PHI_PREDICTIONS':
      return { ...state, phiPredictions: action.payload }

    case 'UPDATE_PHI_PREDICTION': {
      const { sopInstanceUid, index, updates } = action.payload
      const currentPredictions = state.phiPredictions[sopInstanceUid] || []
      const updatedPredictions = [...currentPredictions]
      if (updatedPredictions[index]) {
        updatedPredictions[index] = { ...updatedPredictions[index], ...updates }
      }
      return {
        ...state,
        phiPredictions: {
          ...state.phiPredictions,
          [sopInstanceUid]: updatedPredictions,
        },
      }
    }

    case 'ADD_PHI_PREDICTION': {
      const existingPredictions = state.phiPredictions[action.payload.sopInstanceUid] || []
      return {
        ...state,
        phiPredictions: {
          ...state.phiPredictions,
          [action.payload.sopInstanceUid]: [...existingPredictions, action.payload.prediction],
        },
      }
    }

    case 'REMOVE_PHI_PREDICTION': {
      const filteredPredictions = (state.phiPredictions[action.payload.sopInstanceUid] || []).filter(
        (p) => p.id !== action.payload.predictionId,
      )
      return {
        ...state,
        phiPredictions: {
          ...state.phiPredictions,
          [action.payload.sopInstanceUid]: filteredPredictions,
        },
      }
    }

    case 'TOGGLE_DRAWING_MODE':
      return {
        ...state,
        isDrawingMode: !state.isDrawingMode,
        isEditMode: !state.isDrawingMode ? false : state.isEditMode, // Exit edit mode when entering drawing mode
        currentEditingBox: !state.isDrawingMode ? null : state.currentEditingBox,
        boxesDrawn: false, // Force redraw
      }

    case 'TOGGLE_EDIT_MODE':
      return {
        ...state,
        isEditMode: !state.isEditMode,
        isDrawingMode: state.isEditMode ? state.isDrawingMode : false, // Exit drawing mode when entering edit mode
        currentEditingBox: state.isEditMode ? null : state.currentEditingBox,
        boxesDrawn: false, // Force redraw
      }

    case 'SET_EDIT_MODE':
      return {
        ...state,
        isEditMode: action.payload,
        isDrawingMode: action.payload ? false : state.isDrawingMode,
        currentEditingBox: action.payload ? state.currentEditingBox : null,
        boxesDrawn: false,
      }

    case 'SET_CURRENT_EDITING_BOX':
      return { ...state, currentEditingBox: action.payload }

    case 'SET_SHOW_REDACTIONS':
      return { ...state, showRedactions: action.payload, boxesDrawn: false }

    case 'SET_BOXES_DRAWN':
      return { ...state, boxesDrawn: action.payload }

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }

    case 'SET_IMAGES_LOADED_COUNT':
      return { ...state, imagesLoadedCount: action.payload }

    case 'SET_TOTAL_IMAGES_TO_LOAD':
      return { ...state, totalImagesToLoad: action.payload }

    case 'SET_ALL_IMAGES_LOADED':
      return { ...state, allImagesLoaded: action.payload }

    case 'SET_TOTAL_IMAGES_IN_SERIES':
      return { ...state, totalImagesInSeries: action.payload }

    case 'SET_LOADED_IMAGE_COUNT':
      return { ...state, loadedImageCount: action.payload }

    case 'SET_PHI_WORKFLOW_IN_PROGRESS':
      return { ...state, phiWorkflowInProgress: action.payload }

    case 'SET_CONVERSION_IN_PROGRESS':
      return { ...state, conversionInProgress: action.payload }

    case 'SET_PHI_WORKFLOW_STATUS':
      return { ...state, phiWorkflowStatus: action.payload }

    case 'RESET_LOADING_STATE':
      return {
        ...state,
        imagesLoadedCount: 0,
        totalImagesToLoad: 0,
        loadedImageCount: 0,
        allImagesLoaded: false,
      }

    default:
      return state
  }
}

// Context
interface PHIRedactionContextType {
  state: PHIRedactionState
  dispatch: React.Dispatch<PHIRedactionAction>
}

const PHIRedactionContext = createContext<PHIRedactionContextType | undefined>(undefined)

// Provider component
interface PHIRedactionProviderProps {
  children: ReactNode
}

export function PHIRedactionProvider({ children }: PHIRedactionProviderProps) {
  const [state, dispatch] = useReducer(phiRedactionReducer, initialState)

  return <PHIRedactionContext.Provider value={{ state, dispatch }}>{children}</PHIRedactionContext.Provider>
}

// Hook to use the context
// eslint-disable-next-line react-refresh/only-export-components
export function usePHIRedactionContext() {
  const context = useContext(PHIRedactionContext)
  if (!context) {
    throw new Error('usePHIRedactionContext must be used within a PHIRedactionProvider')
  }
  return context
}
